import { useState } from 'react';
import { Calendar, Inbox, Loader2, Mail, MessageSquare, Phone, Trash2 } from 'lucide-react';
import {
    useDeleteContactMessageMutation,
    useGetContactMessagesQuery,
    useUpdateContactMessageStatusMutation,
} from '../../store/api/apiSlice';
import type { ContactMessage, ContactMessageStatus, ContactMessageType } from '../../types';

const TYPE_LABEL: Record<ContactMessageType, string> = {
    associate: 'Associate enquiry',
    meeting: 'Meeting request',
    general: 'General',
};

const STATUS_STYLE: Record<ContactMessageStatus, string> = {
    new: 'bg-orange-50 text-[#FF6B35]',
    read: 'bg-blue-50 text-blue-600',
    resolved: 'bg-green-50 text-green-700',
};

const FILTERS: { key: 'all' | ContactMessageStatus; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'new', label: 'New' },
    { key: 'read', label: 'Read' },
    { key: 'resolved', label: 'Resolved' },
];

function MessageCard({ message }: { message: ContactMessage }) {
    const [updateStatus] = useUpdateContactMessageStatusMutation();
    const [deleteMessage] = useDeleteContactMessageMutation();

    const created = message.createdAt
        ? new Date(message.createdAt).toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '';

    return (
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50">
                        {message.type === 'meeting' ? (
                            <Calendar className="h-5 w-5 text-[#FF6B35]" />
                        ) : (
                            <MessageSquare className="h-5 w-5 text-[#FF6B35]" />
                        )}
                    </div>
                    <div>
                        <p className="font-semibold text-gray-900">{message.name}</p>
                        <p className="text-xs text-gray-500">
                            {TYPE_LABEL[message.type]}
                            {created && ` · ${created}`}
                        </p>
                    </div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${STATUS_STYLE[message.status]}`}>
                    {message.status}
                </span>
            </div>

            <p className="mb-4 whitespace-pre-wrap text-sm text-gray-700">{message.message}</p>

            <div className="mb-4 flex flex-wrap gap-4 text-sm">
                <a href={`mailto:${message.email}`} className="inline-flex items-center gap-1.5 text-gray-600 hover:text-[#FF6B35]">
                    <Mail className="h-4 w-4" /> {message.email}
                </a>
                {message.phone && (
                    <a href={`tel:${message.phone}`} className="inline-flex items-center gap-1.5 text-gray-600 hover:text-[#FF6B35]">
                        <Phone className="h-4 w-4" /> {message.phone}
                    </a>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
                {message.status !== 'read' && (
                    <button
                        type="button"
                        onClick={() => updateStatus({ id: message.id, status: 'read' })}
                        className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Mark read
                    </button>
                )}
                {message.status !== 'resolved' && (
                    <button
                        type="button"
                        onClick={() => updateStatus({ id: message.id, status: 'resolved' })}
                        className="rounded-full bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
                    >
                        Mark resolved
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => {
                        if (window.confirm('Delete this message?')) deleteMessage(message.id);
                    }}
                    aria-label="Delete message"
                    className="ml-auto rounded-full p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

export default function Messages() {
    const { data: messages, isLoading, isError } = useGetContactMessagesQuery();
    const [filter, setFilter] = useState<'all' | ContactMessageStatus>('all');

    const filtered = (messages ?? []).filter((m) => filter === 'all' || m.status === filter);
    const newCount = (messages ?? []).filter((m) => m.status === 'new').length;

    return (
        <div className="p-4 md:p-8">
            <div className="mb-6">
                <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 md:text-3xl">
                    <Inbox className="h-7 w-7 text-[#FF6B35]" /> Messages
                    {newCount > 0 && (
                        <span className="rounded-full bg-[#FF6B35] px-2 py-0.5 text-xs font-bold text-white">
                            {newCount} new
                        </span>
                    )}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                    Enquiries and meeting requests submitted from the landing page.
                </p>
            </div>

            <div className="mb-6 flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        onClick={() => setFilter(f.key)}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                            filter === f.key
                                ? 'bg-gray-900 text-white'
                                : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
                </div>
            ) : isError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                    Could not load messages.
                </div>
            ) : filtered.length === 0 ? (
                <div className="rounded-2xl border border-gray-100 bg-white p-12 text-center">
                    <Inbox className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                    <p className="font-medium text-gray-900">No messages</p>
                    <p className="mt-1 text-sm text-gray-500">New enquiries will appear here.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {filtered.map((message) => (
                        <MessageCard key={message.id} message={message} />
                    ))}
                </div>
            )}
        </div>
    );
}
